import { isFlagEnabled } from '../backoffice/platformFlagEffective.js';

/** Literal `true` required (same pattern as outreach / Geo / CWV). */
export const SCORECARD_READINESS_ENV = 'SCORECARD_READINESS_ENABLED';
export const VITE_SCORECARD_READINESS_ENV = 'VITE_SCORECARD_READINESS_ENABLED';
export const SCORECARD_READINESS_NURTURE_ENV = 'SCORECARD_READINESS_NURTURE_ENABLED';

export const SCORECARD_COOKIE_NAME = 'apt_readiness_scorecard';
export const SCORECARD_QUERY_PARAM = 'scorecard';
export const SCORECARD_COOKIE_MAX_AGE_SEC = 86400;

export const SCORECARD_LANDING_PATH = '/scorecard/agency-training-readiness';
export const SCORECARD_RESULTS_PATH_PREFIX = '/scorecard/agency-training-readiness/r';
export const SCORECARD_SIGNUP_CHOOSE_PATH = '/signup/choose';

export const SCORECARD_PRODUCT_NAME = 'Agency Training Readiness Score';
export const SCORECARD_HEADLINE = "How ready is your agency's training operation?";
export const SCORECARD_META_DESCRIPTION =
  'Answer 12 quick questions about how your agency publishes training, handles registration, manages instructors, tracks attendance, and maintains completion records. Get a 0–100 readiness score in about 3 minutes.';

export const SCORECARD_LEAD_REUSE_DAYS = 30;

export const READINESS_ACCOUNT_TYPES = [
  'attendee',
  'coordinator',
  'professional',
  'employer',
] as const;

export type ReadinessAccountType = (typeof READINESS_ACCOUNT_TYPES)[number];

export const READINESS_BANDS = ['getting_started', 'building', 'ready_to_run'] as const;
export type ReadinessBand = (typeof READINESS_BANDS)[number];

export const READINESS_DIMENSIONS = [
  'publish',
  'catalog',
  'team',
  'records',
  'follow_through',
] as const;

export type ReadinessDimension = (typeof READINESS_DIMENSIONS)[number];

export const READINESS_EMAIL_CAMPAIGNS = ['receipt', 'nurture_d3', 'nurture_d7'] as const;
export type ReadinessEmailCampaign = (typeof READINESS_EMAIL_CAMPAIGNS)[number];

export function isScorecardReadinessEnabled(
  env: Record<string, string | undefined> = process.env,
): boolean {
  return isFlagEnabled(SCORECARD_READINESS_ENV, env);
}

export function isScorecardReadinessUiEnabled(
  env: Record<string, string | undefined> = process.env,
): boolean {
  return isFlagEnabled(VITE_SCORECARD_READINESS_ENV, env);
}

/** Landing is reachable only when API + Vite flags are both on. */
export function isScorecardReadinessPublicSurfaceEnabled(
  env: Record<string, string | undefined> = process.env,
): boolean {
  return isScorecardReadinessEnabled(env) && isScorecardReadinessUiEnabled(env);
}

export function isScorecardReadinessNurtureEnabled(
  env: Record<string, string | undefined> = process.env,
): boolean {
  return isFlagEnabled(SCORECARD_READINESS_NURTURE_ENV, env);
}

export function normalizeScorecardEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isReadinessAccountType(value: string): value is ReadinessAccountType {
  return (READINESS_ACCOUNT_TYPES as readonly string[]).includes(value);
}

export function resultsPathForToken(token: string): string {
  return `${SCORECARD_RESULTS_PATH_PREFIX}/${encodeURIComponent(token)}`;
}

export function signupHrefForRecommend(
  recommend: ReadinessAccountType | null,
  token: string,
): string {
  const params = new URLSearchParams({ [SCORECARD_QUERY_PARAM]: token });
  if (recommend) params.set('recommend', recommend);
  return `${SCORECARD_SIGNUP_CHOOSE_PATH}?${params.toString()}`;
}

export function bandLabel(band: ReadinessBand): string {
  switch (band) {
    case 'getting_started':
      return 'Getting started';
    case 'building':
      return 'Building';
    case 'ready_to_run':
      return 'Ready to run';
    default: {
      const _exhaustive: never = band;
      return _exhaustive;
    }
  }
}

export function dimensionLabel(dimension: ReadinessDimension): string {
  switch (dimension) {
    case 'publish':
      return 'Training scheduling';
    case 'catalog':
      return 'Course and reporting consistency';
    case 'team':
      return 'Staff responsibilities';
    case 'records':
      return 'Completion records';
    case 'follow_through':
      return 'End-to-end process';
    default: {
      const _exhaustive: never = dimension;
      return _exhaustive;
    }
  }
}

/** Public landing + results token URLs only — not backoffice. */
export function isScorecardReadinessPublicPath(pathname: string): boolean {
  const path = (pathname.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  if (path === SCORECARD_LANDING_PATH) return true;
  if (path.startsWith(`${SCORECARD_RESULTS_PATH_PREFIX}/`)) return true;
  return false;
}
